<template>
      <b-overlay :show="false">
        <form @submit="ctcFormSubmit">
            <fieldset :disabled="!$acl || $acl['cost-to-company'] < 2" style="border: none; padding: 0; margin: 0;">
            
            <!-- Top Section: Outside the card -->
            <div class="settings-top-section mb-4">
                <h4 class="ctc-dashboard-title mb-1">Fixed Benefits</h4>
                <div class="ctc-dashboard-subtitle">Define how remuneration is broken down for CTC calculation</div>
                <div class="salary-type-selector mt-3 mb-2 d-flex align-items-center">
                    <b-form-radio name="salaryType" v-model="salaryAsBreakDownOrGrossValue" :value="1" @change="toggleSalaryAsBreakDownOrGross(1)" class="mr-4">
                        Salary Breakdown
                    </b-form-radio>
                    <b-form-radio name="salaryType" v-model="salaryAsBreakDownOrGrossValue" :value="0" @change="toggleSalaryAsBreakDownOrGross(0)">
                        Gross Salary
                    </b-form-radio>
                </div>
            </div>

            <!-- Main Content Card -->
            <div class="settings-form-card">
                <div v-if="salaryAsBreakDownOrGrossValue == 1" class="mb-4">
                    <div class="section-header-row d-flex justify-content-between align-items-center mb-3">
                        <h5 class="section-title mb-0">Gross Salary Components</h5>
                    </div>
                    <div class="table-container-custom">
                        <table class="table ctc-settings-table mb-0">                  
                            <thead>
                                <tr>
                                    <th :style="{ width: ctcMode == 1 ? '70%' : '50%' }">Salary Heads as per Settings</th>
                                    <th class="text-center">{{ ctcMode == 1 ? 'Show in Report' : 'Show in Annual Letter' }}</th>
                                    <th v-if="ctcMode != 1" class="text-center">Show in Compensation Letter</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="breakDownFixedBenefit in breakDownFixedBenefits" :key="breakDownFixedBenefit.id">
                                    <td>
                                        {{ breakDownFixedBenefit.field_title }}
                                    </td>
                                    <td class="text-center">
                                        <toggle-button :sync="true" :value="!!breakDownFixedBenefit.appear_in_annual" @change="onToggleChange(breakDownFixedBenefit, $event, 'appear_in_annual')"/> 
                                    </td>
                                    <td v-if="ctcMode != 1" class="text-center">
                                        <toggle-button :sync="true" :value="!!breakDownFixedBenefit.appear_in_compensation" @change="onToggleChange(breakDownFixedBenefit, $event, 'appear_in_compensation')"/> 
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="mb-4">
                    <div class="section-header-row d-flex justify-content-between align-items-center mb-3">
                        <h5 class="section-title mb-0">Other Monthly Benefits</h5>
                        <toggle-button :sync="true" :value="!!parentComponentSettingButtons.fixedBenefits" @change="onToggleChange(breakDownFixedBenefits, $event, 'all', 'fixedBenefits')"/>
                    </div>
                    <div v-if="parentComponentSettingButtons.fixedBenefits" class="table-container-custom">
                        <table class="table ctc-settings-table mb-0">
                            <thead>
                                <tr>
                                    <th :style="{ width: ctcMode == 1 ? '70%' : '30%' }">Salary Heads as per Settings</th>
                                    <th class="text-center">{{ ctcMode == 1 ? 'Show in Report' : 'Show in Annual Letter' }}</th>
                                    <th v-if="ctcMode != 1" class="text-center">Show in Compensation Letter</th>
                                    <th v-if="ctcMode != 1" class="text-center">
                                        Ignore Amount 
                                        <b-icon class="ml-1 text-secondary" icon="info-circle" title='If selected "As per Company Policy" text will appear in the letter.' v-b-tooltip></b-icon>
                                        <br> <span class="small font-italic"><i>(Applicable for Compensation Letter)</i></span>
                                    </th>
                                    <th v-if="ctcMode != 1" class="text-center">
                                        Same as Gross Salary 
                                        <b-icon class="ml-1 text-secondary" icon="info-circle" title='If selected Gross Salary will appear in the letter.' v-b-tooltip></b-icon>
                                        <br> <span class="small font-italic"><i>(Applicable for Compensation Letter)</i></span>
                                    </th>
                                </tr>
                            </thead>
                            
                            <tbody>
                                <!-- `v-if` rather than `v-show`: a display:none row still counts for
                                     `tbody tr:nth-child(even)`, which would flip the zebra
                                     striping for every row after it. -->
                                <tr v-for="grossFixedBenefit in grossFixedBenefits" :key="grossFixedBenefit.id" v-if="!(ctcMode == 1 && (grossFixedBenefit.field_key === 'other_perquisite_allowance' || grossFixedBenefit.field_title === 'Other Perquisites, Allowances and Benefits'))">
                                    <td>
                                        {{ grossFixedBenefit.field_title }}
                                    </td>
                                    <td class="text-center">
                                        <toggle-button :sync="true" :value="!!grossFixedBenefit.appear_in_annual" @change="onToggleChange(grossFixedBenefit, $event, 'appear_in_annual', 'fixed')"/> 
                                    </td>
                                    <td v-if="ctcMode != 1" class="text-center">
                                        <toggle-button :sync="true" :value="!!grossFixedBenefit.appear_in_compensation" @change="onToggleChange(grossFixedBenefit, $event, 'appear_in_compensation', 'fixed')"/> 
                                    </td>
                                    <td v-if="ctcMode != 1" class="text-center">
                                        <toggle-button :sync="true" :value="!!grossFixedBenefit.ignore_amount" @change="onToggleChange(grossFixedBenefit, $event, 'ignore_amount', 'fixed')"/> 
                                    </td>
                                    <td v-if="ctcMode != 1" class="text-center">
                                        <toggle-button :disabled="grossFixedBenefit.ignore_amount === 1" :sync="true" :value="!!grossFixedBenefit.club_with_gross" @change="onToggleChange(grossFixedBenefit, $event, 'club_with_gross', 'fixed')"/> 
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
               
                <!-- Variable Benefits -->
                <div v-if="ctcMode != 1" class="mb-4">
                    <div class="section-header-row d-flex justify-content-between align-items-center mb-3">
                        <h5 class="section-title mb-0">Variable Benefits</h5>
                        <toggle-button :sync="true" :value="!!parentComponentSettingButtons.variableBenefits" @change="onToggleChange(variableBenefits, $event, 'all', 'variableBenefits')"/> 
                    </div>
                    
                    <div v-if="parentComponentSettingButtons.variableBenefits" class="table-container-custom">
                        <table class="table ctc-settings-table mb-0">
                            <thead>
                                <tr>
                                    <th style="width: 30%">Salary Heads as per Settings</th>
                                    <th class="text-center">Show in Annual Letter</th>
                                    <th class="text-center">Show in Compensation Letter</th>
                                    <th class="text-center">
                                        Ignore Amount 
                                        <b-icon class="ml-1 text-secondary" icon="info-circle" title='If selected "As per Company Policy" text will appear in the letter.' v-b-tooltip></b-icon>
                                        <br> <span class="small font-italic"><i>(Applicable for Compensation Letter)</i></span>
                                    </th>
                                    <th class="text-center">
                                        Same as Gross Salary 
                                        <b-icon class="ml-1 text-secondary" icon="info-circle" title='If selected Gross Salary will appear in the letter.' v-b-tooltip></b-icon>
                                        <br> <span class="small font-italic"><i>(Applicable for Compensation Letter)</i></span>
                                    </th>
                                </tr>
                            </thead>
                            
                            <tbody>
                                <tr v-for="variableBenefit in variableBenefits" :key="variableBenefit.id">
                                    <td>
                                        {{ variableBenefit.field_title }}
                                    </td>
                                    <td class="text-center">
                                        <toggle-button :sync="true" :value="!!variableBenefit.appear_in_annual" @change="onToggleChange(variableBenefit, $event, 'appear_in_annual')"/> 
                                    </td>
                                    <td class="text-center">
                                        <toggle-button :sync="true" :value="!!variableBenefit.appear_in_compensation" @change="onToggleChange(variableBenefit, $event, 'appear_in_compensation')"/> 
                                    </td>
                                    <td class="text-center">
                                        <toggle-button :sync="true" :value="!!variableBenefit.ignore_amount" @change="onToggleChange(variableBenefit, $event, 'ignore_amount')"/> 
                                    </td>
                                    <td class="text-center">
                                        <toggle-button :disabled="variableBenefit.ignore_amount === 1" :sync="true" :value="!!variableBenefit.club_with_gross" @change="onToggleChange(variableBenefit, $event, 'club_with_gross')"/> 
                                    </td>             
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Contracted Variable Benefits -->
                <div class="mb-4">
                    <div class="section-header-row d-flex justify-content-between align-items-center mb-3">
                        <h5 class="section-title mb-0">Contracted Variable Benefits</h5>
                        <toggle-button :sync="true" :value="!!parentComponentSettingButtons.contractedVariableBenefits" @change="onToggleChange(contractedVariableBenefits, $event, 'all', 'contractedVariableBenefits')"/>
                    </div>
                    <div v-if="parentComponentSettingButtons.contractedVariableBenefits" class="table-container-custom">
                        <table class="table ctc-settings-table mb-0">
                            <thead>
                                <tr>
                                    <th :style="{ width: ctcMode == 1 ? '70%' : '30%' }">Salary Heads as per Settings</th>
                                    <th class="text-center">{{ ctcMode == 1 ? 'Show in Report' : 'Show in Annual Letter' }}</th>
                                    <th v-if="ctcMode != 1" class="text-center">Show in Compensation Letter</th>
                                    <th v-if="ctcMode != 1" class="text-center">
                                        Ignore Amount 
                                        <b-icon class="ml-1 text-secondary" icon="info-circle" title='If selected "As per Company Policy" text will appear in the letter.' v-b-tooltip></b-icon>
                                        <br> <span class="small font-italic"><i>(Applicable for Compensation Letter)</i></span>
                                    </th>
                                    <th v-if="ctcMode != 1" class="text-center">
                                        Same as Gross Salary 
                                        <b-icon class="ml-1 text-secondary" icon="info-circle" title='If selected Gross Salary will appear in the letter.' v-b-tooltip></b-icon>
                                        <br> <span class="small font-italic"><i>(Applicable for Compensation Letter)</i></span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="contractedVariableBenefit in contractedVariableBenefits" :key="contractedVariableBenefit.id">
                                    <td>
                                        {{ contractedVariableBenefit.field_title }}
                                    </td>
                                    <td class="text-center">
                                        <toggle-button :sync="true" :value="!!contractedVariableBenefit.appear_in_annual" @change="onToggleChange(contractedVariableBenefit, $event, 'appear_in_annual')"/> 
                                    </td>
                                    <td v-if="ctcMode != 1" class="text-center">
                                        <toggle-button :sync="true" :value="!!contractedVariableBenefit.appear_in_compensation" @change="onToggleChange(contractedVariableBenefit, $event, 'appear_in_compensation')"/> 
                                    </td>
                                    <td v-if="ctcMode != 1" class="text-center">
                                        <toggle-button :sync="true" :value="!!contractedVariableBenefit.ignore_amount" @change="onToggleChange(contractedVariableBenefit, $event, 'ignore_amount')"/> 
                                    </td>
                                    <td v-if="ctcMode != 1" class="text-center">
                                        <toggle-button :disabled="contractedVariableBenefit.ignore_amount === 1" :sync="true" :value="!!contractedVariableBenefit.club_with_gross" @change="onToggleChange(contractedVariableBenefit, $event, 'club_with_gross')"/> 
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Deductions -->
                <div v-if="ctcMode != 1" class="mb-4">  
                    <div class="section-header-row d-flex justify-content-between align-items-center mb-3">
                        <h5 class="section-title mb-0">Deductions</h5>
                        <toggle-button :sync="true" :value="!!parentComponentSettingButtons.fixedAndVariableDeductions" @change="onToggleChange(fixedDeductions, $event, 'all', 'fixedAndVariableDeductions')"/> 
                    </div>
                    <div v-if="parentComponentSettingButtons.fixedAndVariableDeductions" class="table-container-custom">
                        <table class="table ctc-settings-table mb-0">
                            <thead>
                                <tr>
                                    <th style="width: 30%">Salary Heads as per Settings</th>
                                    <th class="text-center">Show in Annual Letter</th>
                                    <th class="text-center">Show in Compensation Letter</th>
                                    <th class="text-center">
                                        Ignore Amount 
                                        <b-icon class="ml-1 text-secondary" icon="info-circle" title='If selected "As per Company Policy" text will appear in the letter.' v-b-tooltip></b-icon>
                                        <br> <span class="small font-italic"><i>(Applicable for Compensation Letter)</i></span>
                                    </th>
                                </tr>
                            </thead>
                            <!-- Fixed Deductions -->
                            <tbody>
                                <tr v-for="fixedDeduction in fixedDeductions" :key="fixedDeduction.id">
                                    <td>
                                        {{ fixedDeduction.field_title }}
                                    </td>
                                    <td class="text-center">
                                        <toggle-button :sync="true" :value="!!fixedDeduction.appear_in_annual" @change="onToggleChange(fixedDeduction, $event, 'appear_in_annual')"/> 
                                    </td>
                                    <td class="text-center">
                                        <toggle-button :sync="true" :value="!!fixedDeduction.appear_in_compensation" @change="onToggleChange(fixedDeduction, $event, 'appear_in_compensation')"/> 
                                    </td>
                                    <td class="text-center">
                                        <toggle-button :sync="true" :value="!!fixedDeduction.ignore_amount" @change="onToggleChange(fixedDeduction, $event, 'ignore_amount')"/> 
                                    </td>
                                </tr>

                                <!-- Variable Deductions -->
                                <tr v-for="variableDeduction in variableDeductions" :key="variableDeduction.id">
                                    <td>
                                        {{ variableDeduction.field_title }}
                                    </td>
                                    <td class="text-center">
                                        <toggle-button :sync="true" :value="!!variableDeduction.appear_in_annual" @change="onToggleChange(variableDeduction, $event, 'appear_in_annual')"/> 
                                    </td>
                                    <td class="text-center">
                                        <toggle-button :sync="true" :value="!!variableDeduction.appear_in_compensation" @change="onToggleChange(variableDeduction, $event, 'appear_in_compensation')"/> 
                                    </td>
                                    <td class="text-center">
                                        <toggle-button :sync="true" :value="!!variableDeduction.ignore_amount" @change="onToggleChange(variableDeduction, $event, 'ignore_amount')"/> 
                                    </td>
                                </tr>
                            </tbody>    
                        </table>
                    </div>
                </div>

                <!-- Submit Button inside the card at the bottom -->
                <div class="d-flex justify-content-center mt-5" v-if="$acl && $acl['cost-to-company'] == 2">
                    <!-- Same treatment as "Close" on the audit snapshot modal
                         (.ctc-audit-close-btn in AuditReport.vue): the design's
                         195x48 primary block, with the palette stated locally so
                         the secondary hover fill cannot be lost to the cascade. -->
                    <button type="submit" class="ctc-remuneration-submit-btn">
                        Submit
                    </button>
                </div>
            </div>
            </fieldset>
        </form>
      </b-overlay>
</template>

<script>
import ctcOverlay from '../js/ctcOverlay';
export default {
    mixins: [ctcOverlay],
    props: ['pageurl'],
    watch: {
        overlay(newVal) {
            this.ctcOverlay(newVal);
        }
    },
    data () {
        return {
            companyId: '',
            companyAllData: [],
            fixedBenefits: [],
            breakDownFixedBenefits: [],
            grossFixedBenefits: [],
            variableBenefits: [],
            fixedDeductions: [],
            variableDeductions: [],
            contractedVariableBenefits: [],
            salaryAsBreakDownOrGrossValue: 1,
            allCtcSettings: [],
            ctcMode: 2, // Mode 2 is default (Annual & Compensation Letter format)

            parentComponentSettingButtons: {
                fixedBenefits: 1,
                variableBenefits: 1,
                contractedVariableBenefits: 1,
                fixedAndVariableDeductions: 0  // 27-01-2025
            },
            overlay: false,
        }
    },

    created: async function () {
        try {
            this.overlay = true;  // ← start before everything
            await this.getCompany();
            await this.companyAllSettings();
            await this.getCtcSettings();
            await this.getModeSetting();
            await this.getSiteOptionSettings();
            await this.mergeCtcAndCompanySettings();
            this.ctcFormSubmit();
        } catch (error) {
            console.error("Error during initialization:", error);
        } finally {
            this.overlay = false;  // ← always turns off, even on error
        }
    },

    methods: {
        async getModeSetting() {
            try {
                const response = await axios.get(this.$api_url + '/cost-to-company/get-settings');
                if (response.data && response.data.ctcSettings) {
                    this.ctcMode = Number(response.data.ctcSettings.mode || 2);
                }
            } catch (error) {
                console.error('Error fetching mode setting:', error);
            }
        },
        async getCompany () {
            try {
                const response = await axios.get(this.$api_url + '/company/getcompany');
                this.companyId = response.data;
            } catch (error) {
                console.error('Error fetching company:', error);
            }
        },
        async companyAllSettings () {
            try {
                const response = await axios.get(this.$api_url + '/companyallsettings/' + this.companyId);
                const data = response.data.data;
                this.companyAllData = response.data.data;
                this.sctionOnlyAndStatusFilter(data.fixed, 'fixedBenefits');
                this.sctionOnlyAndStatusFilter(data.variable, 'variableBenefits');
                this.sctionOnlyAndStatusFilter(data.fixed_deduction, 'fixedDeductions');
                this.sctionOnlyAndStatusFilter(data.variable_deduction, 'variableDeductions');
                this.sctionOnlyAndStatusFilter(data.contracted_variable_benefits, 'contractedVariableBenefits');
                this.basicAndGrossDistribution(this.fixedBenefits);
            } catch (error) {
                console.error('Error fetching company settings:', error);
            }
        },
        async getCtcSettings() {
            try {
                const response = await axios.get(this.$api_url + '/cost-to-company/get-report-settings');
                this.allCtcSettings = response.data.ctcSettings;
            } catch (error) {
                console.error('Error fetching CTC settings:', error);
            }
        },
        async getSiteOptionSettings() {
            try {
                // No overlay handling here: created() holds it for the whole
                // initialisation chain, and clearing it midway dropped the overlay
                // while the remaining requests were still running.
                const response = await axios.get(this.$api_url + '/cost-to-company/get-report-options');

                // response.data is an object, not an array.
                // Site options are nested under ctc_site_options.
                const siteOptions = response.data.ctc_site_options || {};

                Object.values(siteOptions).forEach(option => {
                    if (option.opt_key === "ctc_fixed_benefits") {
                        this.parentComponentSettingButtons.fixedBenefits = option.status;
                    } else if (option.opt_key === "ctc_variable_benefits") {
                        this.parentComponentSettingButtons.variableBenefits = option.status;
                    } else if (option.opt_key === "ctc_contracted_variable_benefits") {
                        this.parentComponentSettingButtons.contractedVariableBenefits = option.status;
                    } else if (option.opt_key === "ctc_deductions") {
                        this.parentComponentSettingButtons.fixedAndVariableDeductions = option.status;
                    } else if (option.opt_key === "ctc_salary_as_break_down_or_gross") {
                        this.salaryAsBreakDownOrGrossValue = option.status;
                    }
                });
            } catch (error) {
                console.error('Error fetching Site Option Setting: ', error);
            }
        },
        async mergeCtcAndCompanySettings() {  // 27-01-2025
            const settingsMap = Object.fromEntries(
                this.allCtcSettings.map(setting => [String(setting.settings_id), setting])
            );

            const updateBenefits = (settingsName, benefitsArray) => {
                return benefitsArray.map(benefit => ({
                    ...benefit,
                    appear_in_compensation: benefit.appear_in_compensation || '',
                    appear_in_annual: benefit.appear_in_annual || '',
                    club_with_gross: benefit.club_with_gross || '',
                    ignore_amount: benefit.ignore_amount || ''
                })).map(benefit => {
                    const matchingSetting = settingsMap[String(benefit.id)];
                    // Modify the benefit values based on the settingsName
                    if (settingsName === 'breakDownFixedBenefits' || settingsName === 'grossFixedBenefits') {
                        benefit.appear_in_annual = 1;
                        benefit.appear_in_compensation = 1;
                        benefit.ignore_amount = 0;
                        // benefit.club_with_gross = 1;
                        benefit.club_with_gross = 0; // 27-01-2025
                    } else if (settingsName === 'variableBenefits' || settingsName === 'contractedVariableBenefits') {
                        benefit.appear_in_annual = 1;
                        benefit.appear_in_compensation = 1;
                        benefit.club_with_gross = 0;
                        // benefit.ignore_amount = 1;
                        benefit.ignore_amount = 0;  // 27-01-2025
                    } else if (settingsName === 'fixedDeductions' || settingsName === 'variableDeductions') {
                            benefit.appear_in_annual = 0;
                            benefit.appear_in_compensation = 0;
                            benefit.club_with_gross = 0;
                            benefit.ignore_amount = 0;
                    }
                    return {
                        ...benefit,
                        appear_in_compensation: matchingSetting ? matchingSetting.appear_in_compensation : benefit.appear_in_compensation,
                        appear_in_annual: matchingSetting ? matchingSetting.appear_in_annual : benefit.appear_in_annual,
                        club_with_gross: matchingSetting ? matchingSetting.club_with_gross : benefit.club_with_gross,
                        ignore_amount: matchingSetting ? matchingSetting.ignore_amount : benefit.ignore_amount,
                    };
                });
            };

            // Apply to all specified arrays
            this.breakDownFixedBenefits = updateBenefits('breakDownFixedBenefits', this.breakDownFixedBenefits);
            this.grossFixedBenefits = updateBenefits('grossFixedBenefits', this.grossFixedBenefits);
            this.variableBenefits = updateBenefits('variableBenefits', this.variableBenefits);
            this.fixedDeductions = updateBenefits('fixedDeductions', this.fixedDeductions);
            this.variableDeductions = updateBenefits('variableDeductions', this.variableDeductions);
            this.contractedVariableBenefits = updateBenefits('contractedVariableBenefits', this.contractedVariableBenefits);
        },
        sctionOnlyAndStatusFilter: function(values, settingName) {
            const targetArray = this[settingName]; 
            Object.values(values).forEach(element => {
                if (element.section_only === 1) {
                    if (element.child_components) {
                        Object.values(element.child_components).forEach(child => {
                            if (child.status === 1) {
                                child.field_type = element.field_type;
                                targetArray.push(child);
                            }
                        });
                    }
                } else if(element.status === 1) {
                    targetArray.push(element);
                }
            });
        },
        basicAndGrossDistribution: function(fixedBenefits) {
            Object.keys(fixedBenefits).forEach(key => {
                    const element = fixedBenefits[key];

                    if (element.field_access_key && element.field_access_key.indexOf('custom_gross_salary') >= 0) {
                        if(element.field_access_key.startsWith('custom_gross_salary')) {
                            this.breakDownFixedBenefits.push(element);
                        }
                    } else if(element.field_access_key === 'basic_salary' || element.field_access_key === 'house_rent' || element.field_access_key === 'medical_allowance' || element.field_access_key === 'conveyance_allowance') {
                            this.breakDownFixedBenefits.push(element);
                    } 

                    if((element.field_access_key && element.field_access_key.indexOf('custom_gross_salary') >= 0) || (element.field_access_key === 'basic_salary' || element.field_access_key === 'house_rent' || element.field_access_key === 'medical_allowance' || element.field_access_key === 'conveyance_allowance')) {
                        
                    } else {
                        this.grossFixedBenefits.push(element);
                    }
            });
        },
        onToggleChange: function (element, event, column, settingName = null) {
            if (!this.$acl || this.$acl['cost-to-company'] < 2) {
                this.$bvToast.toast('You are not authorized to change settings.', { title: 'Error', variant: 'danger', solid: true });
                return;
            }
            let value = event.value === true ? 1 : 0;
        
            if (column === 'all' && settingName) {
                if (this.parentComponentSettingButtons.hasOwnProperty(settingName)) {
                    this.parentComponentSettingButtons[settingName] = value;
                }
                
                if(settingName === 'fixedBenefits') {
                    this.parentComponentSettingButtons.fixedBenefits = value;
                } else if (settingName === 'variableBenefits') {
                    this.parentComponentSettingButtons.variableBenefits = value;
                } else if (settingName === 'contractedVariableBenefits') {
                    this.parentComponentSettingButtons.contractedVariableBenefits = value;
                } else if (settingName === 'fixedAndVariableDeductions') {
                    this.parentComponentSettingButtons.fixedAndVariableDeductions = value;
                } 
                
                // Mapping between settingName and the corresponding benefits array
                const settingsMap = {
                    fixedBenefits: [this.grossFixedBenefits],
                    variableBenefits: [this.variableBenefits],
                    contractedVariableBenefits: [this.contractedVariableBenefits],
                    fixedAndVariableDeductions: [this.fixedDeductions, this.variableDeductions]
                };

                if(value === 0 ) {
                    const targetArrays = settingsMap[settingName];
                    if (targetArrays) {
                        targetArrays.forEach(benefitsArray => {
                            benefitsArray.forEach(benefit => {
                                benefit.appear_in_annual = value;
                                benefit.appear_in_compensation = value;
                                benefit.club_with_gross = value;
                                if (settingName !== 'fixedBenefits') {
                                    benefit.ignore_amount = value;
                                }
                            });
                        });
                    }
                }
            } else if (column) {
                if (column == 'ignore_amount' && value == 1) {
                    element['club_with_gross'] = 0;
                }
                element[column] = value;
            }
        },
        ctcFormSubmit: function(e) {
            if (e) {
                if (!this.$acl || this.$acl['cost-to-company'] < 2) {
                    this.$bvToast.toast('You are not authorized to save settings.', { title: 'Error', variant: 'danger', solid: true });
                    return;
                }
                e.preventDefault(); // Prevent default only if an event is passed
            }
            // 27-01-2025
            // e.preventDefault();
            let formData = new FormData();
            const arraysToInclude = {
                breakDownFixedBenefits: this.breakDownFixedBenefits,
                grossFixedBenefits: this.grossFixedBenefits,
                variableBenefits: this.variableBenefits,
                fixedDeductions: this.fixedDeductions,
                variableDeductions: this.variableDeductions,
                contractedVariableBenefits: this.contractedVariableBenefits,
                settingButtonAndEvent: this.parentComponentSettingButtons,
            };
            
            // Serialize and add each array to FormData
            Object.keys(arraysToInclude).forEach((key) => {
                formData.append(key, JSON.stringify(arraysToInclude[key]));
            });
            formData.append('salaryAsBreakDownOrGross', this.salaryAsBreakDownOrGrossValue);

            axios.post(this.$api_url + '/cost-to-company/report-settings', formData)
                .then(response => {
                    if(e) {
                        if (response.data.success === true) {
                            this.$bvToast.toast("Settings Updated!!!", {title: 'Success',variant: 'success',solid: true, toaster: 'b-toaster-bottom-left'});
                        } else {
                            this.$bvToast.toast("Invalid Company", {title: 'Error',variant: 'danger',solid: true, toaster: 'b-toaster-bottom-left'});
                        }
                    }
                    
                })
                .catch(error => {
                    console.error('Error submitting form:', error);
                });
            
        },
        toggleSalaryAsBreakDownOrGross: function(value) {
            if (!this.$acl || this.$acl['cost-to-company'] < 2) {
                this.$bvToast.toast('You are not authorized to change settings.', { title: 'Error', variant: 'danger', solid: true });
                return;
            }
            this.salaryAsBreakDownOrGrossValue = value;
        }
    },
    
}
</script>

<style scoped>
.settings-top-section {
    padding: 8px 0 0 4px;
}

.settings-form-card {
    border: 1px solid #E8E8E8 !important;
    border-radius: 12px !important;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05) !important;
    background-color: #ffffff !important;
    padding: 32px 24px !important;
}

/* Page header (.ctc-dashboard-title / .ctc-dashboard-subtitle) is styled
   centrally in resources/sass/_ctc.scss. */

h5, .section-title {
    color: #01524B !important;
    font-weight: 600 !important;
    font-size: 25px !important;
    line-height: 22px !important;
    letter-spacing: -0.02em !important;
    margin-bottom: 0 !important;
    background-color: #ffffff !important;
}

/* The standard CTC table format the design draws: a 55px teal header carrying
   no internal rules, then a full 1px #E8E8E8 grid over 60px rows with the even
   ones striped #F8F8F8. Same treatment as .ctc-config-table on CTC Heads and
   .manual-items-table on CTC Settings, so the three screens read as one design.
   Rounded corners come from the .table-container-custom wrapper. */
.ctc-settings-table {
    width: 100%;
    border-collapse: collapse;
}

.ctc-settings-table thead {
    background-color: #01524B !important;
}

.ctc-settings-table thead th {
    color: #ffffff !important;
    font-weight: 600 !important;
    font-size: 16px !important;
    line-height: 24px !important;
    letter-spacing: -0.02em !important;
    text-align: center !important;
    border: none !important;
    height: 55px;
    padding: 10px !important;
    vertical-align: middle !important;
}

.ctc-settings-table tbody tr {
    transition: background-color 0.2s;
}

.ctc-settings-table tbody td {
    height: 60px;
    padding: 10px 10px 10px 20px !important;
    font-size: 18px !important;
    line-height: 20px !important;
    letter-spacing: -0.02em !important;
    color: #34444C !important;
    font-weight: 400 !important;
    vertical-align: middle !important;
    border: 1px solid #E8E8E8 !important;
}

/* Striping is set on the cell rather than the row so it sits at the same level
   as the hover rule below, which has to target the cell to beat it. */
.ctc-settings-table tbody tr:nth-child(even) td {
    background-color: #F8F8F8;
}

.ctc-settings-table tbody tr:hover td {
    background-color: #f6f6f6 !important;
}

/* Custom styling for vue-js-toggle-button to match #01524B branding. Off state
   is the pale #D5E6EB the design specifies, which is also what .ctc-switch wears
   on the CTC Settings screen; red is reserved there for the mode activation bar,
   so a red off-state here read as an error rather than a plain "not shown". */
::v-deep .vue-js-switch .v-switch-core {
    background-color: #D5E6EB !important;
}
::v-deep .vue-js-switch.toggled .v-switch-core {
    background-color: #01524B !important;
}

/* Disabled toggles stay muted so they don't read as an active "off" choice. */
::v-deep .vue-js-switch.disabled .v-switch-core {
    background-color: #cbd5e1 !important;
}

/* Custom styling for the Salary Breakdown / Gross Salary radios. Mirrors the
   .custom-radio-circle look used in Settings.vue: 20px ring, thin border, white
   fill and a 14px dot when checked — drawn in #0060C9 rather than the CTC teal. */
::v-deep .custom-radio .custom-control-label::before {
    border: 1.5px solid #0060C9 !important;
    background-color: #ffffff !important;
    width: 20px !important;
    height: 20px !important;
    top: 2px !important;
    border-radius: 50% !important;
    box-shadow: none !important;
    transition: all 0.15s ease-in-out;
}

::v-deep .custom-radio .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #ffffff !important;
    border-color: #0060C9 !important;
}

/* The dot is only drawn when checked, centred inside the 20px ring. */
::v-deep .custom-radio .custom-control-input:checked ~ .custom-control-label::after {
    width: 14px !important;
    height: 14px !important;
    top: 5px !important;
    left: calc(-1.5rem + 3px) !important;
    border-radius: 50% !important;
    background-color: #0060C9 !important;
    background-image: none !important;
}

::v-deep .custom-radio .custom-control-input:disabled ~ .custom-control-label::before {
    background-color: #f1f5f9 !important;
    border-color: #cbd5e1 !important;
}

::v-deep .custom-radio .custom-control-label {
    padding-left: 8px !important;
    font-size: 15px !important;
    color: #4b5563 !important;
    line-height: 24px !important;
}

/* Submit button. The global .btn-secondary palette ($admin-primary-btn #004242,
   hovering to the pale #DDF5F9 fill with the #0892A4 edge, per
   .ess-portal.admin-portal in resources/sass/atlhr.scss), restated here as it is
   on .btn-submit-cost in Configuration.vue and .ctc-audit-close-btn in
   AuditReport.vue: this is a raw <button>, so nothing guarantees it picks the
   system hover up. Geometry is the design's 195x48 block. */
.ctc-remuneration-submit-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 195px;
    height: 48px;
    padding: 10px 30px;
    background-color: #004242 !important;
    border: 1.5px solid #004242 !important;
    border-radius: 8px;
    font-weight: 600;
    font-size: 20px;
    line-height: 19px;
    letter-spacing: -0.04em;
    text-transform: capitalize;
    color: #FFFFFF !important;
    transition: all 0.2s;
}

.ctc-remuneration-submit-btn:hover {
    background-color: #DDF5F9 !important;
    border-color: #0892A4 !important;
    color: #004242 !important;
}
</style>

